import React from "react";
import {
  DatePicker,
  DayOfWeek,
  DefaultButton,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  Stack,
  TextField,
  Calendar,
  Callout,
  FocusTrapZone,
  DirectionalHint
} from "@fluentui/react";
import { Formik, FormikErrors } from "formik";
import CustomDrawer from "../common/CustomDrawer";
import * as yup from "yup";
import {
  childrenGap,
  CUSTOMER_CARD_TYPES_DROPDOWN as IMPORT_TYPE_DROPDOWN,
  PDU_TYPE_DROPDOWN,
  ACCESS_LOG_TYPE_DROPDOWN,
  DayPickerStrings,
  FILE_UPLOAD_CONFIG,
  FILE_UPLOAD_MODULES,
} from "../common/Constants";
import {
  getSamplesForManualImport,
  manualImport,
} from "../../services/datacenter/DatacenterService";
import { ToastContext } from "../common/ToastProvider";
import moment from "moment";
import { getFileTypeErrorMessage, validateFileType } from "../../services/UtilService";

const importSchema = yup.object().shape({
  importType: yup.string().required("required"),
  date: yup.date().required("required"),
  file: yup.mixed().required("required"),
  pduType: yup.string().when("importType", {
    is: "pdu",
    otherwise: yup.string().optional(),
    then: yup.string().required("required"),
  }),
  accessLogType: yup.string().when("importType", {
    is: "access-logs",
    otherwise: yup.string().optional(),
    then: yup.string().required("required"),
  }),
});

let calendarButtonElement: HTMLElement;

export interface ManageImportDialogProps {
  handleDialogClose: any;
  datacenterId: string;
  handleLoading: any;
}

const ManageImportDialog: React.FC<ManageImportDialogProps> = ({
  handleDialogClose,
  datacenterId,
  handleLoading,
}) => {
  //state
  const [state, setState] = React.useState({
    importType: "",
    date: new Date(),
    datacenterId: datacenterId,
    file: undefined,
    pduType: "rackwise",
    accessLogType: "card-holders",
  } as {
    importType: string;
    date: Date;
    datacenterId: string;
    file: any;
    pduType?: string;
    accessLogType?: string;
  });
  const [showCallender, setShowCalender] = React.useState(false as boolean);

  //custom hooks
  const { showToast } = React.useContext(ToastContext);
  // file upload config
  const fileUploadConfig = FILE_UPLOAD_CONFIG[FILE_UPLOAD_MODULES.MANAGE_IMPORT_DIALOG];

  const handleFileChange = (files: any) => {
    // console.log(typeof files[0]);

    if (files && files.length > 0) {
      // Validate file type
      const isValidType = validateFileType(
        files[0],
        fileUploadConfig.acceptedFileTypes
      );

      if (!isValidType) {
        showToast(
          getFileTypeErrorMessage(fileUploadConfig.fileTypeDescription),
          "error"
        );
      }
      // console.log("f", files[0]);

      setState({
        ...state,
        file: files[0],
      });
    } else {
      showToast("Select only valid file", "error");
    }
  };

  const handClickDownloadSample = async (obj: any) => {
    // console.log("obj", obj);
    let xlsxRaw = await getSamplesForManualImport(obj);
    let xlsx = await xlsxRaw.blob();
    const xlsxFile = await new Blob([xlsx], {
      type: "application/vnd.openxmlformats",
      // type: "application/csv",
    });
    const xlsxFileURL = await URL.createObjectURL(xlsxFile);
    var a: any = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    a.href = xlsxFileURL;

    let name = "Samples For ";
    switch (obj.importType) {
      case "pdu":
        name += `Power Utilization ${
          obj.pduType === "dedicated" ? "Dedicated" : "Rackwise"
        }`;
        break;
      case "access-logs":
        name += `${
          obj.accessLogType === "card-holders" ? "Card Holders" : "ACS Details"
        } Access Logs`;
        break;
      case "temp-and-rh":
        name += "Temperature & Humidity";
        break;
    }

    a.download = `${name}.xlsx`;
    // a.download = `${name}.csv`;
    a.click();
  };

  return (
    <React.Fragment>
      <CustomDrawer
        title={"Import Manually"}
        handelOpen={true}
        onClose={() => handleDialogClose()}
      >
        <Stack>
          <Formik
            enableReinitialize
            initialValues={state}
            validationSchema={importSchema}
            validate={(values: typeof state) => {
              const errors: FormikErrors<typeof state> = {};
              if (values.file) {
                const isValidType = validateFileType(
                  values.file,
                  fileUploadConfig.acceptedFileTypes
                );

                if (!isValidType) {
                  errors.file = getFileTypeErrorMessage(
                    fileUploadConfig.fileTypeDescription
                  );
                }
              }
              return errors
            }}
            onSubmit={async (values: typeof state, { setSubmitting }) => {
              let obj = { ...values };
              handleLoading();
              let result = await manualImport(obj);
              handleLoading();
              if (result?.success) {
                showToast("Imported successfully", "success");
              } else {
                showToast(result.message || "Error while importing", "error");
              }
              setState({
                ...state,
                importType: "",
                file: undefined,
                datacenterId: "",
              });
              handleDialogClose();
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap}>
                  <Stack.Item>
                    <Stack horizontal wrap tokens={childrenGap}>
                      <Stack.Item grow={1} className="custom-dropdown mb-30">
                        <Dropdown
                          placeholder="Select Import Types"
                          label={"Import Types"}
                          selectedKey={values.importType}
                          options={IMPORT_TYPE_DROPDOWN}
                          onChange={(e, v: IDropdownOption | undefined) => {
                            if (v) {
                              setState({
                                ...state,
                                importType: v.key as string,
                                date: (v.key as string) === "access-logs" ? moment().subtract(1,"month").toDate() : new Date()
                              });
                              //   setFieldValue("importType", v.key);
                            }
                          }}
                          errorMessage={
                            errors.importType && touched.importType
                              ? errors.importType
                              : ""
                          }
                        />
                      </Stack.Item>
                      {values.importType === "pdu" && (
                        <Stack.Item grow={1} className="custom-dropdown mb-30">
                          <Dropdown
                            placeholder="Select PDU Types"
                            label={"PDU Types"}
                            selectedKey={values.pduType}
                            options={PDU_TYPE_DROPDOWN}
                            onChange={(e, v: IDropdownOption | undefined) => {
                              if (v) {
                                setState({
                                  ...state,
                                  pduType: v.key as string,
                                  accessLogType: "",
                                });
                                //   setFieldValue("pduType", v.key);
                              }
                            }}
                            errorMessage={
                              errors.pduType && touched.pduType
                                ? errors.pduType
                                : ""
                            }
                          />
                        </Stack.Item>
                      )}
                      {values.importType === "access-logs" && (
                        <Stack.Item grow={1} className="custom-dropdown mb-30">
                          <Dropdown
                            placeholder="Select Access Logs Types"
                            label={"Access Logs Types"}
                            selectedKey={values.accessLogType}
                            options={ACCESS_LOG_TYPE_DROPDOWN}
                            onChange={(e, v: IDropdownOption | undefined) => {
                              if (v) {
                                setState({
                                  ...state,
                                  accessLogType: v.key as string,
                                  pduType: "",
                                });
                                //   setFieldValue("pduType", v.key);
                              }
                            }}
                            errorMessage={
                              errors.accessLogType && touched.accessLogType
                                ? errors.accessLogType
                                : ""
                            }
                          />
                        </Stack.Item>
                      )}
                    </Stack>
                  </Stack.Item>
                  {values.importType === "pdu" ? (
                      <Stack.Item grow={1} className="mb-30">
                      <div
                        ref={(calendarBtn) =>
                          (calendarButtonElement = calendarBtn!)
                        }
                      >
                        <DefaultButton
                          text={
                            !state.date
                              ? "Select a month"
                              : moment(state.date).format("MMM-YYYY")
                          }
                          onClick={() =>
                            setShowCalender(true)
                          }
                          className="month-picker-btn w-100"
                        />
                      </div>
                      {showCallender && (
                        <Callout
                          isBeakVisible={false}
                          // className="ms-DatePicker-callout"
                          gapSpace={0}
                          doNotLayer={false}
                          target={calendarButtonElement}
                          directionalHint={DirectionalHint.bottomLeftEdge}
                          onDismiss={() => setShowCalender(false)}
                          setInitialFocus
                        >
                          <FocusTrapZone
                            firstFocusableSelector="ms-DatePicker-day--today"
                            isClickableOutsideFocusTrap
                          >
                            <Calendar
                              // eslint-disable-next-line react/jsx-no-bind
                              onSelectDate={(date: Date | null | undefined) => {
                                setState({
                                  ...state,
                                  date: date as any,
                                });
                                setShowCalender(false);
                              }}
                              onDismiss={() => setShowCalender(false)}
                              isMonthPickerVisible={true}
                              value={state.date}
                              firstDayOfWeek={DayOfWeek.Sunday}
                              strings={DayPickerStrings}
                              isDayPickerVisible={false}
                              highlightCurrentMonth={false}
                              highlightSelectedMonth={true}
                              showMonthPickerAsOverlay={false}
                              showGoToToday={false}
                              maxDate={moment().toDate()}
                            />
                          </FocusTrapZone>
                        </Callout>
                      )}
                    </Stack.Item>
                  ) : (
                    <Stack.Item grow={1} className="custom-datepicker">
                    <DatePicker
                      firstDayOfWeek={DayOfWeek.Sunday}
                      showMonthPickerAsOverlay={true}
                      value={new Date(state.date)}
                      showGoToToday={false}
                      onSelectDate={
                        (date: Date | null | undefined) =>
                          setState({
                            ...state,
                            date: date as Date,
                          })
                        // setFieldValue("date", date)
                      }
                      placeholder="Select a date..."
                      ariaLabel="Select a date"
                      label={"Date"}
                    />
                  </Stack.Item>
                  )}
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="file"
                      type="file"
                      accept={fileUploadConfig.acceptedFileTypes}
                      // accept=".csv"
                      onChange={(event: any) =>
                        handleFileChange(event.target.files)
                      }
                      onBlur={handleBlur}
                      label="Select a file"
                      errorMessage={
                        errors.file && touched.file
                          ? (errors.file as string)
                          : ""
                      }
                      description={fileUploadConfig.hintText}
                      className="file-selector"
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                      className="pt-8"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => handleDialogClose()}
                        className="borderless-btn"
                      />
                      <DefaultButton
                        // iconProps={}
                        disabled={values.importType === ""}
                        text="Download Samples"
                        onClick={async () =>
                          await handClickDownloadSample({ ...values })
                        }
                        className="custom-secondary-btn"
                      />
                      <PrimaryButton type="submit" text={"Submit"} />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageImportDialog;
